import React from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate
} from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";
import { PlotProvider } from "./context/PlotContext";
import ProtectedRoute from "./components/ProtectedRoute";

// Landing & Auth
import SplashScreen from "./pages/SplashScreen";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import OTPVerification from "./pages/OTPVerification";
import ChangePassword from "./pages/ChangePassword";

// Customer
import MyFarm from "./pages/MyFarm";
import CustomerMaintenance from "./pages/CustomerMaintenance";
import Billing from "./pages/Billing";
import CustomerIssues from "./pages/CustomerIssues";
import CustomerServiceTickets from "./pages/CustomerServiceTickets";
import CustomerVisits from "./pages/CustomerVisits";
import Messages from "./pages/Messages";
import Referral from "./pages/Referral";
import CustomerNOC from "./pages/CustomerNOC";
import CustomerProfile from "./pages/CustomerProfile";
import CustomerUpdatesFeed from "./pages/CustomerUpdatesFeed";
import CustomerRegistration from "./pages/CustomerRegistration";
import Contact from "./pages/Contact";
import About from "./pages/About";
// Admin
import AdminDashboard from "./pages/AdminDashboard";
import Dashboard from "./pages/Dashboard";
import Users from "./pages/Users";
import Projects from "./pages/Projects";
import Zones from "./pages/Zones";
import Plots from "./pages/Plots";
import Payments from "./pages/Payments";
import Customer from "./pages/Customer";
import Ownership from "./pages/Ownership";
import Subscription from "./pages/Subscription";
import Invoice from "./pages/Invoice";
import Quotations from "./pages/Quotations";
import AdminReferrals from "./pages/AdminReferrals";
import ReferralStatuses from "./pages/ReferralStatuses";
import NOC from "./pages/NOC";
import Supervisor from "./pages/Supervisor";
import Inspection from "./pages/Inspection";
import InspectionPhoto from "./pages/InspectionPhoto";
import AdminLayout from "./pages/AdminLayout";

// New Admin Pages
import Issues from "./pages/Issues";
import ServiceTickets from "./pages/ServiceTickets";
import RegistrationRequests from "./pages/RegistrationRequests";
import Documents from "./pages/Documents";
import Maintenance from "./pages/Maintenance";
import Visits from "./pages/Visits";
import EstateUpdates from "./pages/EstateUpdates";
import Messaging from "./pages/Messaging";
import CRM from "./pages/CRM";
import Staff from "./pages/Staff";
import Reports from "./pages/Reports";
import WaterBore from "./pages/WaterBore";
import ZohoIntegration from "./pages/ZohoIntegration";
import DuePlots from "./pages/DuePlots";

// Supervisor
import SupervisorDashboard from "./pages/SupervisorDashboard";
import AssignedPlots from "./pages/AssignedPlots";
import UploadPhotos from "./pages/UploadPhotos";
import MaintenanceTasks from "./pages/MaintenanceTasks";
import SupervisorVisits from "./pages/SupervisorVisits";


function App() {
  return (
    <AuthProvider>
    <NotificationProvider>
    <PlotProvider>
    <BrowserRouter>
      <Routes>

        {/* Landing */}
        <Route path="/" element={<Login />} />
        <Route path="/splash" element={<SplashScreen />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/otp" element={<OTPVerification />} />
        {/* No allowedRoles — any authenticated user (admin/staff/supervisor/
            customer) can land here, either forced after a temp-password
            login or voluntarily. */}
        <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />

        {/* Admin — protected, admin role only */}
        <Route path="/admin" element={<ProtectedRoute allowedRoles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute allowedRoles={["admin"]}><Dashboard /></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute allowedRoles={["admin"]}><Users /></ProtectedRoute>} />
        <Route path="/projects" element={<ProtectedRoute allowedRoles={["admin"]}><Projects /></ProtectedRoute>} />
        <Route path="/zones" element={<ProtectedRoute allowedRoles={["admin"]}><Zones /></ProtectedRoute>} />
        <Route path="/plots" element={<ProtectedRoute allowedRoles={["admin"]}><Plots /></ProtectedRoute>} />
        <Route path="/water-charges" element={<ProtectedRoute allowedRoles={["admin"]}><WaterBore /></ProtectedRoute>} />
        <Route path="/payments" element={<ProtectedRoute allowedRoles={["admin"]}><Payments /></ProtectedRoute>} />
        <Route path="/customers" element={<ProtectedRoute allowedRoles={["admin"]}><Customer /></ProtectedRoute>} />
        <Route path="/ownership" element={<ProtectedRoute allowedRoles={["admin"]}><Ownership /></ProtectedRoute>} />
        <Route path="/subscriptions" element={<ProtectedRoute allowedRoles={["admin"]}><Subscription /></ProtectedRoute>} />
        <Route path="/invoices" element={<ProtectedRoute allowedRoles={["admin"]}><Invoice /></ProtectedRoute>} />
        <Route path="/quotations" element={<ProtectedRoute allowedRoles={["admin"]}><Quotations /></ProtectedRoute>} />
        <Route path="/admin-referrals" element={<ProtectedRoute allowedRoles={["admin"]}><AdminReferrals /></ProtectedRoute>} />
        <Route path="/referral-statuses" element={<ProtectedRoute allowedRoles={["admin"]}><ReferralStatuses /></ProtectedRoute>} />
        <Route path="/noc" element={<ProtectedRoute allowedRoles={["admin"]}><NOC /></ProtectedRoute>} />
        <Route path="/supervisors" element={<ProtectedRoute allowedRoles={["admin"]}><Supervisor /></ProtectedRoute>} />
        <Route path="/inspections" element={<ProtectedRoute allowedRoles={["admin"]}><Inspection /></ProtectedRoute>} />
        <Route path="/inspection-photos" element={<ProtectedRoute allowedRoles={["admin"]}><InspectionPhoto /></ProtectedRoute>} />

        {/* New Admin */}
        <Route path="/maintenance" element={<ProtectedRoute allowedRoles={["admin", "supervisor"]}><Maintenance /></ProtectedRoute>} />
        <Route path="/issues" element={<ProtectedRoute allowedRoles={["admin"]}><Issues /></ProtectedRoute>} />
        <Route path="/service-tickets" element={<ProtectedRoute allowedRoles={["admin"]}><ServiceTickets /></ProtectedRoute>} />
        <Route path="/registration-requests" element={<ProtectedRoute allowedRoles={["admin"]}><RegistrationRequests /></ProtectedRoute>} />
        <Route path="/documents" element={<ProtectedRoute allowedRoles={["admin", "customer"]}><Documents /></ProtectedRoute>} />
        <Route path="/visits" element={<ProtectedRoute allowedRoles={["admin"]}><Visits /></ProtectedRoute>} />
        <Route path="/estate-updates" element={<ProtectedRoute allowedRoles={["admin"]}><EstateUpdates /></ProtectedRoute>} />
        <Route path="/messaging" element={<ProtectedRoute allowedRoles={["admin"]}><Messaging /></ProtectedRoute>} />
        <Route path="/crm-leads" element={<ProtectedRoute allowedRoles={["admin"]}><CRM /></ProtectedRoute>} />
        <Route path="/staff" element={<ProtectedRoute allowedRoles={["admin"]}><Staff /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute allowedRoles={["admin"]}><Reports /></ProtectedRoute>} />
        <Route path="/zoho-integration" element={<ProtectedRoute allowedRoles={["admin"]}><ZohoIntegration /></ProtectedRoute>} />
        <Route path="/due-plots" element={<ProtectedRoute allowedRoles={["admin"]}><DuePlots /></ProtectedRoute>} />

        {/* Supervisor */}
        <Route
          path="/supervisor-dashboard"
          element={<SupervisorDashboard />}
        />

        <Route
          path="/assigned-plots"
          element={<AssignedPlots />}
        />

        <Route
          path="/upload-photos"
          element={<UploadPhotos />}
        />

        <Route
          path="/maintenance-tasks"
          element={<MaintenanceTasks />}
        />

        <Route
          path="/supervisor-visits"
          element={<SupervisorVisits />}
        />

        <Route path="/my-farm" element={<MyFarm />} />
        <Route path="/farm-maintenance" element={<CustomerMaintenance />} />
        <Route path="/billing" element={<Billing />} />
        {/* Was a separate stub page (CustomerUpdates.js) duplicating this one — redirect old links */}
        <Route path="/customerupdates" element={<Navigate to="/customer-updates" replace />} />
        <Route path="/customer-issues" element={<CustomerIssues />} />
        <Route path="/customer-service-tickets" element={<CustomerServiceTickets />} />
        <Route path="/customervisits" element={<CustomerVisits />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/referral" element={<Referral />} />
        <Route path="/customer-noc" element={<CustomerNOC />} />
        <Route path="/customer-profile" element={<CustomerProfile />} />
       <Route
  path="/customer-updates"element={<CustomerUpdatesFeed />}

/>
      <Route path="/customer-registration" element={<CustomerRegistration />} />
      <Route path="/contact" element={<ProtectedRoute allowedRoles={["customer"]}><Contact /></ProtectedRoute>} />
      <Route path="/about" element={<ProtectedRoute allowedRoles={["customer"]}><About /></ProtectedRoute>} />
      </Routes>
    </BrowserRouter>
    </PlotProvider>
    </NotificationProvider>
    </AuthProvider>
  );
}

export default App;